import { defineStore } from 'pinia'
import { relationships as apiRelationship } from '@services/options'
import { computed, ref } from 'vue'
import { useCachedRequest } from '@composables/useCachedRequest'

export type ApiRelationship = {
  code: string
  module: string
  relationship: string
}

export const useRelationshipCachedStore = defineStore('relationshipCache', () => {
  const cacheKey = ref('relationship_cache')

  const g = async () => {
    const result = await apiRelationship()
    if (!result.status) throw new Error(result.message)
    return result.data
  }

  const {
    data: relationships,
    error,
    message,
    isLoading,
    isReady,
  } = useCachedRequest<ApiRelationship[], string>(cacheKey, g)

  const relationshipName = computed(() => {
    return (relationshipCode: string) =>
      relationships.value?.find((rCode) => rCode.code == relationshipCode)?.relationship ?? relationshipCode
  })

  const relationshipTypes = computed(() => {
    return (gender: string) => {
      const genderToRelationship = {
        MALE: 'HUS',
        FEMALE: 'WIF',
      }
      return relationships.value?.filter(
        (r) => r.code.toUpperCase() != genderToRelationship[gender.toUpperCase() as keyof typeof genderToRelationship],
      )
    }
  })

  const fetchRelationships = async () => {
    error.value = false
    message.value = null
    isLoading.value = true

    return await (async () => {
      const result = await apiRelationship()
      if (result.status) {
        relationships.value = result.data
      } else {
        error.value = true
        message.value = result.message
      }
      isLoading.value = false
    })
  }

  return {
    fetchRelationships,
    relationshipName,
    relationshipTypes,
    relationships,
    error,
    message,
    isLoading,
    isReady,
  }
})

export const useRelationshipStore = defineStore('relationship', {
  state: () => {
    return {
      relationships: undefined as unknown as ApiRelationship[],
      error: false,
      message: null,
      isLoading: false,
    }
  },
  actions: {
    async getRelationships() {
      this.error = false
      this.message = null
      this.isLoading = true
      const result = await apiRelationship()
      if (result.status) {
        this.relationships = result.data
      } else {
        this.error = true
      }
      this.message = result.message
      this.isLoading = false
    },
  },
})
